import React, { useState, useRef } from 'react';
import {
    AppBar,
    Toolbar,
    Typography,
    IconButton,
    Menu,
    MenuItem,
    BottomNavigation,
    BottomNavigationAction,
    Paper,
    Box,
    Avatar,
    ListItemIcon,
    Container,
    LinearProgress
} from '@mui/material';
import {
    AccountCircle,
    Settings as SettingsIcon,
    Logout,
    CloudUpload,
    Info
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { auth } from '../firebase';
import { signOut } from 'firebase/auth';
import { useUpload } from '../contexts/UploadContext';
import { useTranslation } from 'react-i18next';

interface MainLayoutProps {
    children: React.ReactNode;
}

const MainLayout: React.FC<MainLayoutProps> = ({ children }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const { activeUploads } = useUpload();
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const lastTapRef = useRef<number>(0);

    const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleLogout = async () => {
        handleClose();
        await signOut(auth);
        navigate('/login');
    };

    const handleSettings = () => {
        handleClose();
        navigate('/settings');
    };

    const isNavRoute = ['/tasks', '/seeds', '/plants', '/plan', '/logbook', '/settings', '/account'].includes(location.pathname);

    const getPageTitle = () => {
        switch (location.pathname) {
            case '/tasks': return t('nav.dashboard');
            case '/seeds': return t('nav.seeds');
            case '/plants': return t('nav.plants');
            case '/plan': return t('nav.harvest');
            case '/logbook': return t('nav.logbook');
            case '/settings': return t('nav.settings');
            case '/account': return t('nav.account');
            default: return t('app_name');
        }
    };

    const getNavValue = () => {
        const path = location.pathname;
        if (path === '/tasks') return 0;
        if (path === '/seeds') return 1;
        if (path === '/plants') return 2;
        if (path === '/plan') return 3;
        if (path === '/logbook') return 4;
        return -1;
    };

    return (
        <Box sx={{ pb: isNavRoute ? 12 : 4, minHeight: '100vh', bgcolor: '#f8fafc' }}>
            <AppBar
                position="sticky"
                elevation={0}
                sx={{
                    bgcolor: 'rgba(255, 255, 255, 0.85)',
                    backdropFilter: 'blur(12px)',
                    color: 'text.primary',
                    borderBottom: '1px solid rgba(0, 0, 0, 0.05)',
                    zIndex: (theme) => theme.zIndex.appBar
                }}
            >
                <Container maxWidth="xl">
                    <Toolbar disableGutters>
                        <Typography
                            variant="h6"
                            className="serif-text"
                            sx={{
                                flexGrow: 1,
                                fontWeight: 800,
                                color: 'primary.main',
                                letterSpacing: '-0.5px'
                            }}
                        >
                            {getPageTitle()}
                        </Typography>

                        <IconButton
                            size="large"
                            onClick={handleMenu}
                            color="primary"
                            sx={{
                                p: 0.5,
                                bgcolor: 'rgba(76, 175, 80, 0.08)',
                                '&:hover': { bgcolor: 'rgba(76, 175, 80, 0.15)' }
                            }}
                        >
                            <Avatar
                                sx={{
                                    width: 32,
                                    height: 32,
                                    bgcolor: 'primary.main',
                                    fontSize: '0.9rem',
                                    fontWeight: 'bold'
                                }}
                            >
                                {auth.currentUser?.email?.charAt(0).toUpperCase() || <AccountCircle />}
                            </Avatar>
                        </IconButton>
                        <Menu
                            anchorEl={anchorEl}
                            open={Boolean(anchorEl)}
                            onClose={handleClose}
                            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                            PaperProps={{
                                elevation: 4,
                                sx: {
                                    borderRadius: 2,
                                    mt: 1.5,
                                    minWidth: 200,
                                    boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
                                    '&:before': {
                                        content: '""',
                                        display: 'block',
                                        position: 'absolute',
                                        top: 0,
                                        right: 14,
                                        width: 10,
                                        height: 10,
                                        bgcolor: 'background.paper',
                                        transform: 'translateY(-50%) rotate(45deg)',
                                        zIndex: 0,
                                    },
                                },
                            }}
                        >
                            <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid rgba(0,0,0,0.05)' }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                                    {auth.currentUser?.displayName || t('user_menu.gardener')}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {auth.currentUser?.email}
                                </Typography>
                            </Box>
                            <MenuItem onClick={() => { handleClose(); navigate('/account'); }} sx={{ py: 1.5 }}>
                                <ListItemIcon>
                                    <AccountCircle fontSize="small" color="primary" />
                                </ListItemIcon>
                                {t('user_menu.account')}
                            </MenuItem>
                            <MenuItem onClick={handleSettings} sx={{ py: 1.5 }}>
                                <ListItemIcon>
                                    <SettingsIcon fontSize="small" color="primary" />
                                </ListItemIcon>
                                {t('user_menu.settings')}
                            </MenuItem>
                            <MenuItem onClick={() => { handleClose(); window.open('https://pocket.garden/imprint.html', '_blank'); }} sx={{ py: 1.5 }}>
                                <ListItemIcon>
                                    <Info fontSize="small" color="primary" />
                                </ListItemIcon>
                                {t('user_menu.imprint')}
                            </MenuItem>
                            <MenuItem onClick={handleLogout} sx={{ py: 1.5, color: 'error.main' }}>
                                <ListItemIcon>
                                    <Logout fontSize="small" color="error" />
                                </ListItemIcon>
                                {t('user_menu.logout')}
                            </MenuItem>
                        </Menu>
                    </Toolbar>
                </Container>
            </AppBar>

            {activeUploads.length > 0 && (
                <Box sx={{
                    position: 'sticky',
                    top: 64, // AppBar height
                    left: 0,
                    right: 0,
                    zIndex: (theme) => theme.zIndex.drawer,
                    bgcolor: 'rgba(255, 255, 255, 0.9)',
                    backdropFilter: 'blur(8px)',
                    borderBottom: '1px solid rgba(0, 0, 0, 0.05)',
                    px: 2,
                    py: 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2
                }}>
                    <CloudUpload color="primary" fontSize="small" />
                    <Typography variant="caption" sx={{ flexGrow: 1, fontWeight: 600 }}>
                        {t('upload.uploading', { count: activeUploads.length })}
                    </Typography>
                    <Box sx={{ width: 100 }}>
                        <LinearProgress variant="indeterminate" sx={{ borderRadius: 1 }} />
                    </Box>
                </Box>
            )}

            <Box component="main">
                {children}
            </Box>

            {isNavRoute && (
                <Paper
                    sx={{
                        position: 'fixed',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        width: '100%',
                        zIndex: (theme) => theme.zIndex.appBar,
                        borderTop: '1px solid rgba(0, 0, 0, 0.05)',
                        boxShadow: '0 -4px 20px rgba(0,0,0,0.05)',
                        bgcolor: 'rgba(255, 255, 255, 0.95)',
                        backdropFilter: 'blur(12px)'
                    }}
                    elevation={0}
                >
                    <BottomNavigation
                        showLabels
                        value={getNavValue()}
                        onChange={(event, newValue) => {
                            // If tapping the currently active tab
                            if (newValue === getNavValue()) {
                                const now = Date.now();
                                const timeDiff = now - lastTapRef.current;
                                if (timeDiff < 300) {
                                    window.location.reload();
                                }
                                lastTapRef.current = now;
                                return;
                            }

                            if (newValue !== -1) {
                                const paths = ['/tasks', '/seeds', '/plants', '/plan', '/logbook'];
                                navigate(paths[newValue]);
                            }
                        }}
                        sx={{
                            height: 80,
                            bgcolor: 'transparent',
                            '& .MuiBottomNavigationAction-root': {
                                color: 'text.secondary',
                                minWidth: 'auto',
                                py: 1,
                                '& .MuiBottomNavigationAction-label': {
                                    fontFamily: '"Lora", serif',
                                    fontSize: '0.75rem',
                                    transition: 'color 0.2s ease-in-out',
                                    '&.Mui-selected': {
                                        fontSize: '0.75rem',
                                    }
                                },
                                '&.Mui-selected': {
                                    color: 'primary.main',
                                    '& .nav-icon': {
                                        transform: 'scale(1.1)',
                                        filter: 'grayscale(0%)',
                                    },
                                },
                            },
                        }}
                    >
                        <BottomNavigationAction
                            label={t('nav.home')}
                            icon={
                                <Box
                                    component="img"
                                    src="/icons/tasks.webp"
                                    className="nav-icon"
                                    sx={{ width: 48, height: 48, mb: 0.5, transition: 'transform 0.2s', filter: 'grayscale(100%) opacity(0.7)' }}
                                />
                            }
                        />
                        <BottomNavigationAction
                            label={t('nav.seeds')}
                            icon={
                                <Box
                                    component="img"
                                    src="/icons/seeds.webp"
                                    className="nav-icon"
                                    sx={{ width: 48, height: 48, mb: 0.5, transition: 'transform 0.2s', filter: 'grayscale(100%) opacity(0.7)' }}
                                />
                            }
                        />
                        <BottomNavigationAction
                            label={t('nav.plants')}
                            icon={
                                <Box
                                    component="img"
                                    src="/icons/plants.webp"
                                    className="nav-icon"
                                    sx={{ width: 48, height: 48, mb: 0.5, transition: 'transform 0.2s', filter: 'grayscale(100%) opacity(0.7)' }}
                                />
                            }
                        />
                        <BottomNavigationAction
                            label={t('nav.harvest')}
                            icon={
                                <Box
                                    component="img"
                                    src="/icons/harvest.webp"
                                    className="nav-icon"
                                    sx={{ width: 48, height: 48, mb: 0.5, transition: 'transform 0.2s', filter: 'grayscale(100%) opacity(0.7)' }}
                                />
                            }
                        />

                        <BottomNavigationAction
                            label={t('nav.logbook')}
                            icon={
                                <Box
                                    component="img"
                                    src="/icons/logbook.webp"
                                    className="nav-icon"
                                    sx={{ width: 48, height: 48, mb: 0.5, transition: 'transform 0.2s', filter: 'grayscale(100%) opacity(0.7)' }}
                                />
                            }
                        />
                    </BottomNavigation>
                </Paper>
            )}
        </Box>
    );
};

export default MainLayout;
